import environment from '../config/environment';
import {useCallback, useEffect} from 'react';

type GeetestValidationResult = {
  [key: string]: unknown,
  captcha_output: string,
  gen_time: string,
  lot_number: string,
  pass_token: string,
};

type GeetestCaptchaObj = {
  appendTo: (selector: string) => GeetestCaptchaObj,
  getValidate: () => null | GeetestValidationResult,
  onBoxShow: (callback: () => void) => GeetestCaptchaObj,
  onError: (callback: (error: unknown) => void) => GeetestCaptchaObj,
  onNextReady: (callback: () => void) => GeetestCaptchaObj,
  onReady: (callback: () => void) => GeetestCaptchaObj,
  onSuccess: (callback: () => void) => GeetestCaptchaObj,
  showCaptcha: () => void,
};

type GeetestCaptchaProps = {
  config: {},
  handler: (captchaObj: GeetestCaptchaObj) => void,
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    captchaObj: GeetestCaptchaObj;
    initGeetest4: (
      config: GeetestCaptchaProps['config'],
      handler: GeetestCaptchaProps['handler'],
    ) => void;
  }
}

const geetestScriptId = 'geetest-captcha-script';

export const useGeetestCaptcha = (
  shouldSkip: boolean,
) => {
  // const [validationResult2, setValidationResult] = useState<any>(undefined);
  const captchaConfig = {
    // as discussed with GeeTest account manager, as we have free plan with limited number (2) of actions (events), we don't name our actions as login/forgotPassword but prod and non-prod
    captchaId: environment.geetest.action,
    language: 'eng',
    product: 'bind',
    protocol: 'https://',
  };

  // let validationResult: undefined | null | GeetestValidationResult;
  const captchaHandler = useCallback((captchaObj: GeetestCaptchaObj) => {
    window.captchaObj = captchaObj;
    captchaObj.appendTo('#captcha').onReady(() => {
      // eslint-disable-next-line no-console
      console.log('[GEETEST] ready');
    });
  }, []);

  useEffect(() => {
    if (shouldSkip) {
      return () => {};
    }

    // eslint-disable-next-line unicorn/prefer-query-selector
    let script = document.getElementById(geetestScriptId) as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://static.geetest.com/v4/gt4.js';
      script.async = true;
      script.id = geetestScriptId;
      document.body.appendChild(script);

      script.addEventListener('load', () => {
        window.initGeetest4(captchaConfig, captchaHandler);
      });
    }

    return () => {
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, [shouldSkip]);

  // if returns null means it's an error
  const execute = async () => {
    return new Promise<null | undefined | {} | GeetestValidationResult>(
      (resolve) => {
        if (shouldSkip) {
          resolve({});
        }

        let validationResult: undefined | null | GeetestValidationResult;

        if (window.captchaObj) {
          window.captchaObj.showCaptcha();
          window.captchaObj.onSuccess(() => {
            validationResult = window.captchaObj.getValidate();
          }).onError(() => {
            validationResult = null;
          });

          const interval = setInterval(() => {
            if (validationResult) {
              clearInterval(interval);
              resolve({
                captchaId: captchaConfig.captchaId,
                captchaOutput: validationResult.captcha_output,
                genTime: validationResult.gen_time,
                lotNumber: validationResult.lot_number,
                passToken: validationResult.pass_token,
              });
            } else if (validationResult === null) {
              clearInterval(interval);
              resolve(null);
            }
          }, 300);
        } else {
          resolve(null);
        }
      },
    );
  };

  const captchaRendered = shouldSkip ? null : <div className='captcha' />;

  return {
    captchaRendered,
    execute,
    isExecutingRecaptcha: false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setShowCaptchaV2: (_: boolean) => {},
  };
};
